import { useState } from "react";
import { ArrowRight, Route, Share2 } from "lucide-react";
import type { ForwardingMode, ForwardingSettings } from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { forwardingErrorMessage } from "@/lib/forwarding";
import {
  useSaveForwardingSettings,
  useSetForwardingRoutes,
  type ForwardingChannel,
  type PlatformRoute,
} from "@/lib/hooks/useForwarding";
import { InlineError, PlatformBadge } from "./controls";

interface ModeRoutesCardProps {
  readonly settings: ForwardingSettings;
  readonly channels: readonly ForwardingChannel[];
  readonly routes: readonly PlatformRoute[];
}

const MODES: ReadonlyArray<{
  value: ForwardingMode;
  title: string;
  text: string;
  icon: typeof Share2;
}> = [
  {
    value: "simple",
    title: "Simples",
    text: "Toda oferta de qualquer origem vai para todos os destinos ativos.",
    icon: Share2,
  },
  {
    value: "advanced",
    title: "Avançado",
    text: "Você escolhe, por origem, quais destinos recebem. Origem sem rota não envia.",
    icon: Route,
  },
];

function DestinationPicker({
  destinations,
  draft,
  onChange,
}: {
  destinations: readonly ForwardingChannel[];
  draft: readonly string[];
  onChange: (refs: string[]) => void;
}): JSX.Element {
  return (
    <div className="flex flex-wrap gap-1.5">
      {destinations.length === 0 && (
        <span className="text-xs text-muted-foreground">
          Nenhum destino ativo nesta plataforma.
        </span>
      )}
      {destinations.map((d) => {
        const on = draft.includes(d.ref);
        return (
          <button
            key={d.id}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(on ? draft.filter((r) => r !== d.ref) : [...draft, d.ref])}
            className={cn(
              "rounded-full border px-2.5 py-0.5 text-xs transition-colors",
              on
                ? "border-primary/40 bg-primary/15 text-primary"
                : "border-border text-muted-foreground hover:text-foreground",
            )}
          >
            {d.title ?? d.ref}
          </button>
        );
      })}
    </div>
  );
}

function RouteRow({
  source,
  destinations,
  selected,
  onSave,
}: {
  source: ForwardingChannel;
  destinations: readonly ForwardingChannel[];
  selected: readonly string[];
  onSave: (refs: string[]) => Promise<void>;
}): JSX.Element {
  const [draft, setDraft] = useState<string[]>([...selected]);
  const dirty = [...draft].sort().join() !== [...selected].sort().join();
  return (
    <li className="grid gap-3 py-3 md:grid-cols-[minmax(0,14rem)_auto_1fr_auto] md:items-center">
      <div className="flex min-w-0 items-center gap-2">
        <PlatformBadge platform={source.platform} />
        <span className="truncate text-sm font-medium text-foreground">
          {source.title ?? source.ref}
        </span>
      </div>
      <ArrowRight size={15} className="hidden text-muted-foreground md:block" aria-hidden="true" />
      <DestinationPicker destinations={destinations} draft={draft} onChange={setDraft} />
      <Button size="sm" variant="secondary" disabled={!dirty} onClick={() => void onSave(draft)}>
        Salvar rota
      </Button>
    </li>
  );
}

function ModeOption({
  mode,
  active,
  disabled,
  onSelect,
}: {
  mode: (typeof MODES)[number];
  active: boolean;
  disabled: boolean;
  onSelect: () => void;
}): JSX.Element {
  const Icon = mode.icon;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        "flex items-start gap-3 rounded-xl border p-4 text-left transition-all",
        active
          ? "border-primary/50 bg-primary/[0.07] shadow-[0_0_0_1px_hsl(var(--primary)/0.25)]"
          : "border-border hover:border-primary/25 hover:bg-[hsl(var(--surface-raised)/0.5)]",
      )}
    >
      <Icon
        size={18}
        className={cn("mt-0.5 shrink-0", active ? "text-primary" : "text-muted-foreground")}
        aria-hidden="true"
      />
      <span>
        <span className="block text-sm font-semibold text-foreground">{mode.title}</span>
        <span className="mt-0.5 block text-xs text-muted-foreground">{mode.text}</span>
      </span>
    </button>
  );
}

type RunAction = (action: () => Promise<void>) => Promise<void>;

function RoutesEditor({
  channels,
  routes,
  run,
}: {
  channels: readonly ForwardingChannel[];
  routes: readonly PlatformRoute[];
  run: RunAction;
}): JSX.Element {
  const setRoutes = useSetForwardingRoutes();
  const sources = channels.filter((c) => c.role === "origem");
  const destinationsOf = (platform: ForwardingChannel["platform"]) =>
    channels.filter(
      (c) => c.role === "destino" && c.status === "active" && c.platform === platform,
    );
  if (sources.length === 0) {
    return (
      <p className="mt-2 text-sm text-muted-foreground">
        Marque algum canal como origem para criar rotas.
      </p>
    );
  }
  return (
    <ul className="divide-y divide-border/60">
      {sources.map((source) => (
        <RouteRow
          key={source.id}
          source={source}
          destinations={destinationsOf(source.platform)}
          selected={routes
            .filter((r) => r.platform === source.platform && r.sourceRef === source.ref)
            .map((r) => r.destinationRef)}
          onSave={(refs) =>
            run(() =>
              setRoutes.mutateAsync({
                platform: source.platform,
                sourceRef: source.ref,
                destinationRefs: refs,
              }),
            )
          }
        />
      ))}
    </ul>
  );
}

/** Seletor de modo simples/avancado + editor de rotas por origem. */
export function ModeRoutesCard({ settings, channels, routes }: ModeRoutesCardProps): JSX.Element {
  const saveSettings = useSaveForwardingSettings();
  const [error, setError] = useState<string | null>(null);

  const run = async (action: () => Promise<void>): Promise<void> => {
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(forwardingErrorMessage(err));
    }
  };

  return (
    <section
      className="space-y-4 rounded-xl border border-border bg-card p-5"
      aria-labelledby="mode-title"
    >
      <h2 id="mode-title" className="font-semibold text-foreground">
        Modo de encaminhamento
      </h2>
      <div
        className="grid gap-3 sm:grid-cols-2"
        role="radiogroup"
        aria-label="Modo de encaminhamento"
      >
        {MODES.map((mode) => (
          <ModeOption
            key={mode.value}
            mode={mode}
            active={settings.mode === mode.value}
            disabled={saveSettings.isPending}
            onSelect={() =>
              void run(() => saveSettings.mutateAsync({ ...settings, mode: mode.value }))
            }
          />
        ))}
      </div>
      <InlineError message={error} />
      {settings.mode === "advanced" && (
        <div>
          <p className="label-editorial">Rotas por origem</p>
          <RoutesEditor channels={channels} routes={routes} run={run} />
        </div>
      )}
    </section>
  );
}
